import { Check, Copy } from 'lucide-react';
import { useEffect, useState } from 'react';

import { interactive } from '@/components/ui/interactive';
import { cn } from '@/lib/utils';

export interface CopyButtonProps {
  /** What lands on the clipboard — a phone number, an email, never a label. */
  value: string;
  /** Accessible name for the control, e.g. "Copy the email". Always a translated string. */
  label: string;
  /** What the control announces once the copy has happened. */
  copiedLabel: string;
  className?: string;
}

/** How long the check mark stays before the control reads as a copy button again. */
const COPIED_FOR_MS = 1800;

/**
 * A copy-to-clipboard affordance for a contact detail — the phone and email on a
 * support ticket, the official email on an organization card.
 *
 * It confirms in place, swapping the icon for a check and its own accessible name
 * for `copiedLabel`, rather than firing a toast. A toast would need the `Toaster`
 * above every screen that shows a contact, and the confirmation belongs next to
 * the value that was copied anyway — the admin's eyes are already there.
 *
 * `navigator.clipboard` is absent on an insecure origin and in some embedded
 * browsers; the control still renders and the click then does nothing visible,
 * which beats a crash on a screen whose job is to show the number.
 */
export function CopyButton({ value, label, copiedLabel, className }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), COPIED_FOR_MS);
    return () => window.clearTimeout(timer);
  }, [copied]);

  return (
    <button
      type="button"
      aria-label={copied ? copiedLabel : label}
      aria-live="polite"
      onClick={() => {
        const clipboard = navigator.clipboard;
        if (!clipboard) return;
        void clipboard.writeText(value).then(() => setCopied(true));
      }}
      className={cn(
        interactive,
        'grid h-7 w-7 shrink-0 place-items-center rounded-md text-conc-400 hover:bg-sideline hover:text-navy',
        copied && 'text-pitch hover:text-pitch',
        className,
      )}
    >
      {copied ? (
        <Check aria-hidden className="h-3.5 w-3.5" strokeWidth={2.2} />
      ) : (
        <Copy aria-hidden className="h-3.5 w-3.5" strokeWidth={1.9} />
      )}
    </button>
  );
}
